import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { languageNames, supportedLanguages, forceChangeLanguage } from '../i18n/config';

interface LanguageSelectorProps {
  className?: string;
  variant?: 'dropdown' | 'button';
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({ 
  className = '', 
  variant = 'dropdown' 
}) => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // 현재 언어
  const currentLanguage = i18n.language;
  const currentLanguageName = languageNames[currentLanguage as keyof typeof languageNames] || '🇺🇸 English';

  // 언어 변경 핸들러 (모바일 앱과 동일한 로직)
  const handleLanguageChange = async (languageCode: string) => {
    console.log('🌐 Language change requested:', languageCode);
    console.log('🌐 Current language before change:', i18n.language);
    
    try {
      // 모바일 앱과 동일한 forceChangeLanguage 함수 사용
      await forceChangeLanguage(languageCode);
      console.log('🌐 Language changed successfully to:', i18n.language);
      
      // 강제 리렌더링을 위한 상태 업데이트
      window.dispatchEvent(new Event('languageChanged'));
      
      // 페이지 새로고침으로 확실한 변경
      setTimeout(() => {
        window.location.reload();
      }, 300);
      
    } catch (error) {
      console.error('🌐 Language change failed:', error);
      // 에러 발생 시에도 페이지 새로고침
      window.location.reload();
    }
    
    setIsOpen(false);
    console.log('🌐 Language change completed:', languageCode);
  };

  // 드롭다운 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // 버튼 스타일 변형
  if (variant === 'button') {
    return (
      <div className={`relative ${className}`} ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <span>{currentLanguageName}</span>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {isOpen && (
          <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
            {supportedLanguages.map((langCode) => (
              <button
                key={langCode}
                onClick={() => handleLanguageChange(langCode)}
                className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                  currentLanguage === langCode ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                }`}
              >
                {languageNames[langCode as keyof typeof languageNames]}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // 기본 드롭다운 스타일
  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
      >
        <span className="text-lg">{currentLanguageName.split(' ')[0]}</span>
        <span className="hidden sm:inline">{currentLanguageName.split(' ').slice(1).join(' ')}</span>
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-xl z-50 max-h-80 overflow-y-auto">
          <div className="py-1">
            {supportedLanguages.map((langCode) => (
              <button
                key={langCode}
                onClick={() => handleLanguageChange(langCode)}
                className={`w-full text-left px-4 py-3 text-sm hover:bg-gray-50 transition-colors ${
                  currentLanguage === langCode ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-500' : 'text-gray-700'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <span className="text-lg">{languageNames[langCode as keyof typeof languageNames].split(' ')[0]}</span>
                  <span>{languageNames[langCode as keyof typeof languageNames].split(' ').slice(1).join(' ')}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
